/**
 * Void Personality — mood-driven text lines for the void entity.
 *
 * The void has three mood tiers: calm (0-25), agitated (26-55), enraged (56-100).
 * Higher mood = more aggressive text, bigger font, higher chance of speaking.
 * Text selection uses Math.random() — visual-only, no multiplayer sync needed.
 */

import { VOID_PERSONALITY } from '../constants';

// ─── Mood Tier ────────────────────────────────────────────────

export type MoodTier = 'calm' | 'agitated' | 'enraged';

export function getMoodTier(mood: number): MoodTier {
  if (mood <= VOID_PERSONALITY.CALM_MAX) return 'calm';
  if (mood <= VOID_PERSONALITY.AGITATED_MAX) return 'agitated';
  return 'enraged';
}

export function getMoodFontSize(mood: number): number {
  const tier = getMoodTier(mood);
  if (tier === 'calm') return VOID_PERSONALITY.TEXT_SIZE_CALM;
  if (tier === 'agitated') return VOID_PERSONALITY.TEXT_SIZE_AGITATED;
  return VOID_PERSONALITY.TEXT_SIZE_ENRAGED;
}

// ─── Line Pools ───────────────────────────────────────────────

const KILL_LINES_CALM = [
  'CONSUMED',
  'DEVOURED',
  'TAKEN',
];

const KILL_LINES_AGITATED = [
  'FEED ME',
  'MORE',
  'DELICIOUS',
  'MINE NOW',
  'YES',
];

const KILL_LINES_ENRAGED = [
  'I HUNGER',
  'NOT ENOUGH',
  'ALL OF YOU',
  'INSIGNIFICANT',
  'COME CLOSER',
  'INEVITABLE',
];

const CONSUME_LINES_CALM = [
  'mmm',
  '...',
];

const CONSUME_LINES_AGITATED = [
  'MORE',
  'AGAIN',
  'YES',
  'CLOSER',
];

const CONSUME_LINES_ENRAGED = [
  'FEED ME',
  'EVERYTHING',
  'NOT ENOUGH',
  'I GROW',
  'SUBMIT',
];

const RETALIATION_LINES = [
  'ENOUGH',
  'MY TURN',
  'RETALIATION',
  'YOU DARE?',
  'INSOLENT',
  'FEEL MY WRATH',
];

// Math.random() is intentional — text selection is visual-only (announce_world effects).
// Each client can show a different quip with no desync risk. Per CLAUDE.md RNG rule:
// visual-only code uses Math.random(), game-state code uses seeded random().
function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

// ─── Public API ───────────────────────────────────────────────

/** Get a kill line based on void mood. */
export function getKillLine(mood: number): string {
  const tier = getMoodTier(mood);
  if (tier === 'calm') return pick(KILL_LINES_CALM);
  if (tier === 'agitated') return pick(KILL_LINES_AGITATED);
  return pick(KILL_LINES_ENRAGED);
}

/** Roll for a consume line (may return null if mood-based RNG doesn't trigger). */
export function rollConsumeLine(mood: number): string | null {
  const tier = getMoodTier(mood);
  let chance: number;
  let pool: string[];
  if (tier === 'calm') {
    chance = VOID_PERSONALITY.TEXT_CHANCE_CALM;
    pool = CONSUME_LINES_CALM;
  } else if (tier === 'agitated') {
    chance = VOID_PERSONALITY.TEXT_CHANCE_AGITATED;
    pool = CONSUME_LINES_AGITATED;
  } else {
    chance = VOID_PERSONALITY.TEXT_CHANCE_ENRAGED;
    pool = CONSUME_LINES_ENRAGED;
  }
  if (Math.random() > chance) return null;
  return pick(pool);
}

/** Get a retaliation line for big volleys. */
export function getRetaliationLine(): string {
  return pick(RETALIATION_LINES);
}
